import { Link } from "react-router-dom";
import { Download, Trophy, Clock, MapPin, ChevronRight, Fish } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

export default function FishingTournament() {
  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551942296-97384c850440?w=1800&auto=format&fit=crop"
          alt="Lake"
          className="absolute inset-0 w-full h-full object-cover slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A3161]/70 via-[#0A3161]/50 to-[#0A3161]" />
        <div className="relative h-full flex items-end pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full text-white fade-up">
            <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#FFFFFF] mb-4">
              Freedom Ain't Free
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95]">
              Annual Bass
              <br />
              <span className="italic text-[#FFFFFF]">Fishing Tournament</span>
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-white/85 max-w-2xl">
              Flipper's Bait & Tackle  |  200 Odoms Bend Rd., Gallatin, TN
            </p>
          </div>
        </div>
      </section>

      {/* Prize banner */}
      <section className="bg-[#B22234] text-white py-12 relative overflow-hidden">
        <div className="absolute inset-0 stars opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-6">
            <Trophy className="w-14 h-14 text-[#FFFFFF]" />
            <div>
              <p className="font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">
                Grand Prize
              </p>
              <p className="font-display text-4xl md:text-5xl font-bold">$2,000 Guaranteed First Place</p>
            </div>
          </div>
          <div className="text-center md:text-right">
            <p className="font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">Entry</p>
            <p className="font-display text-3xl font-bold">$100 per boat</p>
            <p className="text-sm text-white/75">$110 day of tournament</p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Tournament Details"
              title="Cast For A Cause On Old Hickory Lake"
              description="Every registration and sponsorship dollar directly supports Sumner County veterans and their families. Whether you're a weekend angler or a tournament regular, we welcome you on the water."
            />

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Clock, label: "Registration", value: "4:15 AM" },
                { icon: Fish, label: "Species", value: "Bass" },
                { icon: MapPin, label: "Launch", value: "Flipper's B&T" },
                { icon: Trophy, label: "1st Place", value: "$2,000" },
              ].map((s, i) => (
                <div key={i} className="border border-[#D9DDE3] p-6 bg-white">
                  <s.icon className="w-6 h-6 text-[#B22234]" />
                  <p className="mt-4 font-condensed uppercase tracking-widest text-xs text-[#374151]">
                    {s.label}
                  </p>
                  <p className="mt-1 font-display text-2xl text-[#0A3161] font-bold">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-[#EEF1F6] border-l-4 border-[#B22234] p-6">
              <p className="font-condensed uppercase tracking-widest text-xs text-[#B22234]">
                What To Know
              </p>
              <ul className="mt-4 space-y-2 text-[#0A3161]">
                {[
                  "Open to anglers of all skill levels",
                  "Standard tournament rules apply. Details in the official packet",
                  "Weigh-in at tournament conclusion at Flipper's Bait & Tackle",
                  "All proceeds assist veterans and families in Sumner County",
                ].map((t, k) => (
                  <li key={k} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 bg-[#B22234] rounded-full shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#packet"
                className="inline-flex items-center gap-2 bg-[#B22234] hover:bg-[#8f1b2a] text-white px-8 h-12 font-condensed uppercase tracking-widest text-xs"
              >
                <Download className="w-4 h-4" /> Official Entry Packet
              </a>
              <Link
                to="/activities"
                className="inline-flex items-center gap-2 border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161] hover:text-white px-8 h-12 font-condensed uppercase tracking-widest text-xs transition-colors"
              >
                All Activities <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-32 space-y-6">
              <div className="bg-[#0A3161] text-white p-8">
                <p className="font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">
                  Contact
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold">Chapter 240 Office</h3>
                <p className="mt-4 text-white/80">Tournament Information</p>
                <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
                  <a href="tel:8885480085" className="block text-white hover:text-[#FFFFFF]">
                    888-548-0085
                  </a>
                </div>
              </div>
              <div className="relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551942296-97384c850440?w=800&auto=format&fit=crop"
                  alt="Bass fishing"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
